// ==========================================================================
// SignalFix v2 — Sample elements
// --------------------------------------------------------------------------
// Representative demo data for the v2 taxonomy so the new icons, segmented
// geometry, legend and clusters are visible before the backend `/signal/all`
// endpoint starts returning these element types. Coordinates follow the
// mockups (Via Aurelia, Pietra Ligure). Merged into the live signs in
// SignalFix.jsx, guarded by SHOW_V2_SAMPLE and deduped by external_id.
//
// The backend seed script (scripts/seed_signalfix_v2.py) inserts the same
// set, so behaviour is identical once real data is available.
// ==========================================================================

// Base point roughly on Via Aurelia; small offsets keep everything on-screen.
const BASE_LAT = 44.1505;
const BASE_LNG = 8.2805;
const o = (dLat, dLng) => [BASE_LAT + dLat, BASE_LNG + dLng];

// A short stretch of road used for the segmented elements' geometry.
const roadLine = [
  o(0.0006, -0.0028),
  o(0.0002, -0.0012),
  o(-0.0002, 0.0004),
  o(-0.0007, 0.002),
  o(-0.0012, 0.0036),
];
const offsetLine = (line, dLat, dLng) => line.map(([lat, lng]) => [lat + dLat, lng + dLng]);

const point = (id, [lat, lng], type, label, extra = {}) => ({
  id,
  external_id: id,
  lat,
  lng,
  type,
  description: label,
  category: "bbox",
  image_url: "",
  street: "Via Aurelia, Pietra Ligure (SV)",
  is_sample: true,
  ...extra,
});

export const SIGNAL_FIX_V2_SAMPLE = [
  // ---- Point elements (elementi_strada_bbox) ----
  point("SV_2024_0001", o(0.0009, -0.0026), "lanterna_semaforica", "Lanterna semaforica"),
  point("SV_2024_0002", o(0.0004, -0.0018), "tombino", "Tombino"),
  point("SV_2024_0003", o(0.0001, -0.0008), "griglia_caditoia", "Griglia / Caditoia"),
  point("SV_2024_0004", o(-0.0001, 0.0002), "dosso", "Dosso"),
  point("SV_2024_0005", o(-0.0004, 0.0012), "buca", "Buca"),
  point("SV_2024_0006", o(-0.0007, 0.0022), "paletto_dissuasore", "Paletto dissuasore"),
  point("SV_2024_0007", o(-0.001, 0.003), "bidone", "Bidone"),
  point("SV_2024_0008", o(-0.0013, 0.0038), "cartellone_pubblicitario", "Cartellone pubblicitario"),

  // ---- Point-rendered segmented elements (poles) ----
  point("SV_2024_0009", o(0.0007, -0.0022), "palo_luce", "Palo luce", { category: "seg" }),
  point("SV_2024_0010", o(-0.0009, 0.0026), "palo_cartello", "Palo cartello", {
    category: "seg",
    support: "palo cartello",
  }),
  point("SV_2024_0011", o(-0.0014, 0.0042), "palo_pubblicita", "Palo pubblicità", { category: "seg" }),

  // ---- A couple of vertical signs to populate clusters ----
  point("SV_2024_0012", o(0.0011, -0.0031), "limite-vel-50", "Limite velocità 50", {
    category: "vertical",
    support: "palo cartello",
  }),
  point("SV_2024_0013", o(-0.0003, 0.0009), "stop", "Stop", { category: "vertical" }),

  // ---- Segmented elements with geometry (elementi_strada_seg) ----
  {
    id: "SV_2024_0101",
    external_id: "SV_2024_0101",
    lat: BASE_LAT - 0.0006,
    lng: BASE_LNG + 0.0018,
    type: "new_jersey",
    description: "New Jersey",
    category: "seg",
    image_url: "",
    street: "Via Aurelia, Pietra Ligure (SV)",
    is_sample: true,
    geometry: offsetLine(roadLine, -0.00012, 0.00012),
  },
  {
    id: "SV_2024_0102",
    external_id: "SV_2024_0102",
    lat: BASE_LAT + 0.0002,
    lng: BASE_LNG - 0.0006,
    type: "marciapiede",
    description: "Marciapiede",
    category: "seg",
    image_url: "",
    street: "Via Aurelia, Pietra Ligure (SV)",
    is_sample: true,
    geometry: offsetLine(roadLine, 0.00014, -0.00014),
  },
  {
    id: "SV_2024_0103",
    external_id: "SV_2024_0103",
    lat: BASE_LAT - 0.001,
    lng: BASE_LNG + 0.0028,
    type: "guardrail",
    description: "Guardrail",
    category: "seg",
    image_url: "",
    street: "Via Aurelia, Pietra Ligure (SV)",
    is_sample: true,
    geometry: offsetLine(roadLine.slice(2), -0.00024, 0.00024),
  },

  // ---- Horizontal signage with geometry (drawn on the carriageway) ----
  {
    id: "SV_2024_0201",
    external_id: "SV_2024_0201",
    lat: BASE_LAT + 0.00035,
    lng: BASE_LNG - 0.0011,
    type: "attraversamento-pedonale",
    description: "Attraversamento pedonale",
    category: "horizontal",
    Segnaletica: true,
    image_url: "",
    street: "Via Aurelia, Pietra Ligure (SV)",
    is_sample: true,
    // Two endpoints spanning the carriageway -> rendered as a zebra band.
    geometry: [o(0.0005, -0.00118), o(0.00022, -0.00102)],
    geometryKind: "crosswalk",
  },
  {
    id: "SV_2024_0202",
    external_id: "SV_2024_0202",
    lat: BASE_LAT - 0.00015,
    lng: BASE_LNG + 0.00035,
    type: "dare-precedenza",
    description: "Linea di arresto / Dare precedenza",
    category: "horizontal",
    Segnaletica: true,
    image_url: "",
    street: "Via Aurelia, Pietra Ligure (SV)",
    is_sample: true,
    geometry: [o(-0.00003, 0.0003), o(-0.00028, 0.0004)],
    geometryKind: "stopline",
  },
  {
    id: "SV_2024_0203",
    external_id: "SV_2024_0203",
    lat: BASE_LAT - 0.0005,
    lng: BASE_LNG + 0.0015,
    type: "freccia-diritto",
    description: "Freccia direzionale",
    category: "horizontal",
    Segnaletica: true,
    image_url: "",
    street: "Via Aurelia, Pietra Ligure (SV)",
    is_sample: true,
    geometry: [o(-0.00062, 0.00142), o(-0.00042, 0.0016)],
    geometryKind: "arrow",
  },
];

export default SIGNAL_FIX_V2_SAMPLE;
